import { useState, useCallback, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import NFCCapture   from './screens/NFCCapture.jsx'
import POSSimulator from './screens/POSSimulator.jsx'
import SmartLedger  from './screens/SmartLedger.jsx'
import LiveChannel  from './screens/LiveChannel.jsx'
import { useItemizSession } from './hooks/useItemizSession.js'
import { authHeaders } from './lib/session.js'
import { mergeTokens, receiptDocToToken, coreTxnToToken } from './lib/ledgerUtils.js'

const TABS = [
  { id: 'capture',   label: 'Capture',   icon: '📡' },
  { id: 'pos',       label: 'Merchant',  icon: '🏪' },
  { id: 'channel',   label: 'Live',      icon: '📶' },
  { id: 'ledger',    label: 'Ledger',    icon: '🗂️' },
]

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit:  (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
}

export default function App() {
  const { ready, error: sessionError, userId } = useItemizSession()
  const [tab, setTab]       = useState('capture')
  const [prevTab, setPrev]  = useState('capture')
  const [tokens, setTokens] = useState([])
  const [pending, setPending] = useState(null)
  const [ledgerLoading, setLedgerLoading] = useState(true)

  const direction = TABS.findIndex(t => t.id === tab) - TABS.findIndex(t => t.id === prevTab)

  const navigate = useCallback((id) => {
    setPrev(tab)
    setTab(id)
  }, [tab])

  const addToken = useCallback((token) => {
    setTokens(prev => [token, ...prev.filter(t => t.id !== token.id)])
  }, [])

  const loadLedger = useCallback(async () => {
    if (!ready) return
    setLedgerLoading(true)
    try {
      const res = await fetch('/api/v1/core/history', { headers: authHeaders() })
      if (res.ok) {
        const data = await res.json()
        const fromReceipts = (data.receipts || []).map(receiptDocToToken)
        const fromCore = (data.core_transactions || []).map(coreTxnToToken)
        setTokens(mergeTokens(fromReceipts, fromCore))
      } else {
        const hist = await fetch('/api/v1/history', { headers: authHeaders() })
        if (hist.ok) {
          const receipts = await hist.json()
          setTokens(mergeTokens(receipts.map(receiptDocToToken)))
        }
      }
    } catch {
      /* keep in-session tokens */
    } finally {
      setLedgerLoading(false)
    }
  }, [ready])

  useEffect(() => {
    loadLedger()
  }, [loadLedger, ready])

  if (!ready) {
    return (
      <motion.div
        className="flex flex-col items-center justify-center h-screen bg-[#080c10] text-white/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          className="w-8 h-8 border-2 border-[#00ff87] border-t-transparent rounded-full mb-4"
          animate={{ rotate: 360 }}
          transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
        />
        <p className="text-sm font-semibold tracking-wider">Starting secure session…</p>
      </motion.div>
    )
  }

  const screens = {
    capture:  <NFCCapture navigate={navigate} setPending={setPending} pending={pending} addToken={addToken} onSettled={loadLedger} userId={userId} />,
    pos:      <POSSimulator navigate={navigate} />,
    channel:  <LiveChannel navigate={navigate} setPending={setPending} />,
    ledger:   <SmartLedger tokens={tokens} loading={ledgerLoading} sessionError={sessionError} onRefresh={loadLedger} />,
  }

  return (
    <div className="flex flex-col h-screen max-w-[430px] mx-auto select-none">
      <motion.div className="flex-1 overflow-hidden relative" layout>
        <AnimatePresence initial={false} custom={direction} mode="popLayout">
          <motion.div
            key={tab}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            className="absolute inset-0 overflow-y-auto pb-[76px]"
            style={{ scrollbarWidth: 'none' }}
          >
            {screens[tab]}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <nav className="absolute bottom-0 inset-x-0 max-w-[430px] mx-auto h-[72px] bg-[#0e1420] border-t border-white/[0.06] flex z-50">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => navigate(t.id)}
            className={`flex-1 flex flex-col items-center justify-center gap-1 text-[10px] font-semibold tracking-wider uppercase border-none bg-none transition-colors duration-200 ${
              tab === t.id ? 'text-[#00ff87]' : 'text-white/30'
            }`}
          >
            <span className={`text-[22px] transition-transform duration-200 ${tab === t.id ? 'scale-110' : ''}`}
                  style={tab === t.id ? {filter:'drop-shadow(0 0 8px rgba(0,255,135,0.6))'} : {}}>
              {t.icon}
            </span>
            {t.label}
          </button>
        ))}
      </nav>
    </div>
  )
}
